import { differenceInDays, parseISO } from 'date-fns'

import { ProgressBar } from '@/components/ui'
import { IS_PRODUCTION } from '@/config'
import { useChatUsage, useSummary } from '@/modules/account'
import { fromNow } from '@/utils'

const Label = ({ children }: { children: React.ReactNode }) => {
  return <span className="text-cp-grey-700 text-xs">{children}</span>
}

const EXPIRY_HINT_THRESHOLD_DAYS = 30

// Only surface the expiry hint when a credit pool is close to expiring.
// Far-future expiries are noise; null (no active purchases) is silent.
const expiresSoon = (expiry: Date | string | null): boolean => {
  if (!expiry) return false
  const target = typeof expiry === 'string' ? parseISO(expiry) : expiry
  const days = differenceInDays(target, new Date())
  return days >= 0 && days <= EXPIRY_HINT_THRESHOLD_DAYS
}

export const ChatUsage = () => {
  const summary = useSummary()
  const chatUsage = useChatUsage()

  // Three branches per CHAT_USAGE_CAP_PLAN.md § 14:
  //   A. capped       → counter + progress + cycle, optional credits line below
  //   B. wallet-only  → credits balance as the primary line, no progress bar
  //   C. uncapped no-wallet → hide the chat block entirely (Phase 1 dark-ship)
  // Production-only: chat usage stays hidden on dev and preview/staging.
  const showChat =
    IS_PRODUCTION && (chatUsage.hasCap || chatUsage.walletBalanceCents > 0)
  const showExpiryHint =
    chatUsage.walletBalanceCents > 0 && expiresSoon(chatUsage.walletExpiresAt)

  if (!showChat) return

  return (
    <>
      <div className="border-t border-slate-200" />
      <div className="flex flex-col gap-1">
        <Label>Chat</Label>
        <ProgressBar
          progress={chatUsage.progress}
          theme={
            chatUsage.progress === chatUsage.minProgress ? 'error' : 'success'
          }
          bordered
        />
        {chatUsage.hasCap && (
          <Label>Resets in {fromNow(summary?.chatRenewCycle)}</Label>
        )}
        {chatUsage.walletBalanceCents > 0 && (
          <Label>Credits available{showExpiryHint && ' · expires soon'}</Label>
        )}
      </div>
    </>
  )
}
